import { useState, useRef, useCallback, useEffect } from 'react'
import { patchDprSection } from './_requests'

export type DprSaveStatus = 'IDLE' | 'SAVING' | 'SAVED' | 'ERROR' | 'LOCKED'

export const useDprAutoSave = (
  reportId?: number,
  onSaveSuccess?: (updatedDto: any) => void,
  debounceMs: number = 600
) => {
  const [saveStatus, setSaveStatus] = useState<DprSaveStatus>('IDLE')
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null)
  const [lastError, setLastError] = useState<string | null>(null)

  const pendingTimersRef = useRef<{ [section: string]: NodeJS.Timeout }>({})
  const pendingPayloadsRef = useRef<{ [section: string]: any }>({})

  // ── Perform actual REST patch ──
  const executeSave = useCallback(
    async (sectionName: string, payload: any) => {
      if (!reportId) return
      setSaveStatus('SAVING')
      setLastError(null)

      try {
        const result = await patchDprSection(reportId, sectionName, payload)
        setSaveStatus('SAVED')
        setLastSavedTime(new Date())
        onSaveSuccess?.(result)
        delete pendingPayloadsRef.current[sectionName]
      } catch (err: any) {
        console.error(`Auto-save failed for ${sectionName}:`, err)
        if (err?.response?.status === 409 || err?.message?.includes('locked')) {
          setSaveStatus('LOCKED')
          setLastError(err?.response?.data?.message || err.message || 'Section is locked by another user')
        } else {
          setSaveStatus('ERROR')
          setLastError(err?.message || 'Failed to auto-save')
        }
      }
    },
    [reportId, onSaveSuccess]
  )

  // ── Debounced trigger ──
  const triggerAutoSave = useCallback(
    (sectionName: string, payload: any, immediate: boolean = false) => {
      if (!reportId) return

      pendingPayloadsRef.current[sectionName] = payload

      if (pendingTimersRef.current[sectionName]) {
        clearTimeout(pendingTimersRef.current[sectionName])
      }

      if (immediate) {
        executeSave(sectionName, payload)
      } else {
        setSaveStatus('SAVING')
        pendingTimersRef.current[sectionName] = setTimeout(() => {
          executeSave(sectionName, pendingPayloadsRef.current[sectionName])
        }, debounceMs)
      }
    },
    [reportId, debounceMs, executeSave]
  )

  // ── Flush all pending saves on unmount ──
  useEffect(() => {
    return () => {
      Object.keys(pendingTimersRef.current).forEach((section) => {
        clearTimeout(pendingTimersRef.current[section])
      })
    }
  }, [])

  return {
    saveStatus,
    lastSavedTime,
    lastError,
    triggerAutoSave,
    setSaveStatus,
  }
}
